import { useEffect } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, HttpError } from '../api/client';
import {
  ActivityIcon, AlertIcon, BoxIcon, ChartIcon, ChevronRightIcon, ClockIcon,
  FolderIcon, KeyIcon, LogoutIcon, UsersIcon, ZapIcon,
} from './icons';

export function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();

  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: api.listProjects,
    staleTime: 60_000,
    retry: (count, err) => !(err instanceof HttpError && err.status === 401) && count < 1,
  });
  const currentProject = projects.data?.find((p) => p.id === projectId);

  const unauthorized = projects.error instanceof HttpError && projects.error.status === 401;
  useEffect(() => {
    if (unauthorized) api.logout().catch(() => {});
  }, [unauthorized]);
  if (unauthorized) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  const onLogout = async () => {
    await api.logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-canvas text-slate-900 flex">
      {/* Sidebar: fixed left rail. ~232px on lg+, icon-only on md, hidden on
          smartphones. Replaces the horizontal nav which got too long. */}
      <aside className="hidden md:flex flex-col w-16 lg:w-56 shrink-0 border-r border-slate-200
                        bg-white/70 backdrop-blur sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-2 h-14 px-4 border-b border-slate-200">
          <span className="h-7 w-7 rounded-lg bg-gradient-to-br from-accent-500 to-accent-700
                           text-white grid place-items-center shadow-sm shadow-accent-500/40 shrink-0">
            <ZapIcon size={16} />
          </span>
          <span className="hidden lg:inline font-semibold tracking-tight text-slate-900">fmon</span>
        </Link>

        {currentProject && (
          <div className="hidden lg:block px-4 py-3 border-b border-slate-200">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Project</p>
            <p className="text-sm font-medium text-slate-900 truncate mt-0.5" title={currentProject.name}>
              {currentProject.name}
            </p>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          <NavItem to="/" end label="Projects" icon={FolderIcon} />

          {projectId && (
            <>
              <Group label="Inspect" />
              <NavItem to={`/projects/${projectId}/live`} label="Live" icon={ActivityIcon}
                       badge={<span className="ml-auto inline-block w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />} />
              <NavItem to={`/projects/${projectId}/errors`}   label="Errors"   icon={AlertIcon} />
              <NavItem to={`/projects/${projectId}/sessions`} label="Sessions" icon={UsersIcon} />
              <NavItem to={`/projects/${projectId}/users`}    label="Users"    icon={UsersIcon} />

              <Group label="Analyse" />
              <NavItem to={`/projects/${projectId}/trends`}      label="Trends"      icon={ChartIcon} />
              <NavItem to={`/projects/${projectId}/performance`} label="Performance" icon={ActivityIcon} />
              <NavItem to={`/projects/${projectId}/slow`}        label="Slow"        icon={ClockIcon} />

              <Group label="Manage" />
              <NavItem to={`/projects/${projectId}/releases`} label="Releases" icon={BoxIcon} />
              <NavItem to={`/projects/${projectId}/settings`} label="Settings" icon={KeyIcon} />
            </>
          )}

          <div className="pt-3 mt-3 border-t border-slate-200">
            <NavItem to="/system" label="System" icon={BoxIcon} />
          </div>
        </nav>

        <div className="border-t border-slate-200 p-2">
          <button onClick={onLogout}
                  className="w-full inline-flex items-center gap-2 lg:gap-3 text-sm text-slate-500
                             hover:text-slate-900 hover:bg-slate-100 h-9 px-3 rounded-lg transition">
            <LogoutIcon size={14} className="shrink-0" />
            <span className="hidden lg:inline">Logout</span>
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        {/* Mobile top bar — only shown on <md when sidebar is hidden */}
        <header className="md:hidden border-b border-slate-200 bg-white sticky top-0 z-10 h-14 px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-lg bg-gradient-to-br from-accent-500 to-accent-700
                             text-white grid place-items-center">
              <ZapIcon size={16} />
            </span>
            <span className="font-semibold tracking-tight">fmon</span>
            {currentProject && (
              <>
                <ChevronRightIcon size={12} className="text-slate-300" />
                <span className="text-sm text-slate-700 truncate max-w-[140px]">{currentProject.name}</span>
              </>
            )}
          </Link>
          <button onClick={onLogout} className="text-slate-500"><LogoutIcon size={16} /></button>
        </header>

        <main className="mx-auto max-w-7xl px-6 py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function Group({ label }: { label: string }) {
  return (
    <p className="hidden lg:block text-[10px] uppercase tracking-wider text-slate-400 font-semibold
                  px-3 pt-4 pb-1">
      {label}
    </p>
  );
}

function NavItem({
  to, end = false, label, icon: Icon, badge,
}: {
  to: string;
  end?: boolean;
  label: string;
  icon: typeof FolderIcon;
  badge?: React.ReactNode;
}) {
  return (
    <NavLink to={to} end={end}
             title={label}
             className={({ isActive }) =>
               'flex items-center gap-2 lg:gap-3 h-9 px-2 lg:px-3 rounded-lg transition text-sm ' +
               (isActive
                 ? 'bg-slate-900 text-white shadow-sm'
                 : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100')
             }>
      <Icon size={15} className="shrink-0" />
      <span className="hidden lg:inline truncate">{label}</span>
      {badge}
    </NavLink>
  );
}
